import { DocumentActions } from '@/components/DocumentActions'
import {
  Alert,
  Button,
  Card,
  CardContent,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useState } from 'react'
import { SendDocumentIcon } from '@/components/SendDocument'
import { useDecideRefund, useProcessRefund, useRefunds, useReverseRefund } from '@/api/hooks'
import { RaiseRefundDialog } from './RaiseRefundDialog'
import { useAuthStore } from '@/auth/authStore'
import type { Refund } from '@/api/types'
import { EmptyState, ErrorBanner, formatDate, formatMoney, StatusChip } from '@/components/common'

const STATUSES = ['REQUESTED', 'APPROVED', 'PROCESSED', 'REJECTED', 'REVERSED']

/**
 * Money owed back to customers.
 *
 * <p>Most of these raise themselves: an order cancelled with an advance paid,
 * or a head count that came down after the customer had settled in full. What
 * is left for a person is the part only a person can do — agreeing it, and
 * making the transfer.</p>
 *
 * <p>Which is the point of the screen. A refund the system worked out but
 * nobody can see is the same as no refund at all, from where the customer is
 * standing.</p>
 */
export function RefundsTab() {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const [status, setStatus] = useState('REQUESTED')
  const { data, isLoading, error } = useRefunds(status || undefined)
  const decide = useDecideRefund()
  const [paying, setPaying] = useState<Refund | null>(null)
  const [raising, setRaising] = useState(false)
  const [reversing, setReversing] = useState<Refund | null>(null)

  return (
    <>
      <ErrorBanner error={error ?? decide.error} />

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            sx={{ minWidth: 220 }}
            helperText="Requested ones are waiting on somebody"
          >
            <MenuItem value="">All</MenuItem>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {value.charAt(0) + value.slice(1).toLowerCase()}
              </MenuItem>
            ))}
          </TextField>
          {canWrite('payment') ? (
            <Button variant="contained" onClick={() => setRaising(true)}>
              Raise a refund
            </Button>
          ) : null}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        {isLoading && <LinearProgress />}
        {data && data.length === 0 ? (
          <EmptyState message="Nothing owed back at the moment." />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Voucher</TableCell>
                <TableCell>Raised</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Why</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Withheld</TableCell>
                <TableCell align="right">To pay</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {(data ?? []).map((refund) => (
                <TableRow key={refund.id} hover>
                  <TableCell>{refund.refundNumber}</TableCell>
                  <TableCell>{formatDate(refund.refundDate)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{refund.customerName ?? '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {refund.orderNumber ?? ''}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 380 }}>
                    <Typography variant="body2">{refund.reason}</Typography>
                  </TableCell>
                  <TableCell align="right">{formatMoney(refund.amount)}</TableCell>
                  <TableCell align="right">{formatMoney(refund.deductionAmount)}</TableCell>
                  <TableCell align="right">{formatMoney(refund.netPayable)}</TableCell>
                  <TableCell>
                    <StatusChip status={refund.status} />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {/* Printable at any stage: the voucher is what the person
                          authorising the transfer signs, and it says on its face
                          whether it is a request, an authority or a payment. */}
                      <DocumentActions
                        path={`/api/v1/refunds/${refund.id}/pdf`}
                        fileName={`${refund.refundNumber.replace(/\//g, '-')}.pdf`}
                        label="voucher"
                      />
                      <SendDocumentIcon kind="refund-voucher" id={refund.id} />
                      {refund.status === 'REQUESTED' && can('refund.approve') ? (
                        <>
                          <Button
                            size="small"
                            color="error"
                            disabled={decide.isPending}
                            onClick={() =>
                              decide.mutate({
                                id: refund.id,
                                body: { approved: false, remarks: 'Declined' },
                              })
                            }
                          >
                            Decline
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={decide.isPending}
                            onClick={() =>
                              decide.mutate({
                                id: refund.id,
                                body: { approved: true, remarks: 'Approved' },
                              })
                            }
                          >
                            Approve
                          </Button>
                        </>
                      ) : null}
                      {refund.status === 'APPROVED' && canWrite('payment') ? (
                        <Button size="small" variant="contained" onClick={() => setPaying(refund)}>
                          Mark paid
                        </Button>
                      ) : null}
                      {refund.status === 'PROCESSED' ? (
                        <Typography variant="caption" color="text.secondary">
                          {refund.bankReference}
                        </Typography>
                      ) : null}
                      {/* Raised against the wrong order, or marked paid against
                          a transfer that never went. Undone, not deleted. */}
                      {refund.status !== 'REVERSED' && can('payment.reverse') ? (
                        <Button size="small" color="error" onClick={() => setReversing(refund)}>
                          Reverse
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <RaiseRefundDialog open={raising} onClose={() => setRaising(false)} />
      <ReverseRefundDialog refund={reversing} onClose={() => setReversing(null)} />
      <PayRefundDialog refund={paying} onClose={() => setPaying(null)} />
    </>
  )
}

/**
 * Undoing a refund that should never have been raised.
 *
 * <p>It does not move money. What it says is that this refund was a mistake,
 * beside the reason it was a mistake - and it stops the entry absorbing an
 * excess the customer is genuinely owed.</p>
 */
function ReverseRefundDialog({ refund, onClose }: { refund: Refund | null; onClose: () => void }) {
  const reverse = useReverseRefund()
  const [reason, setReason] = useState('')

  return (
    <Dialog open={Boolean(refund)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reverse {refund?.refundNumber}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={reverse.error} />
        <Alert severity="warning" sx={{ mb: 2 }}>
          {formatMoney(refund?.amount ?? 0)} against {refund?.orderNumber ?? 'this order'}. The
          entry stays on file with your reason; no money moves either way.
        </Alert>
        <TextField
          fullWidth
          required
          multiline
          minRows={2}
          size="small"
          label="Why it should not have been raised"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Go back</Button>
        <Button
          variant="contained"
          color="error"
          disabled={!reason.trim() || reverse.isPending}
          onClick={() =>
            refund &&
            reverse.mutate(
              { id: refund.id, reason: reason.trim() },
              {
                onSuccess: () => {
                  setReason('')
                  onClose()
                },
              },
            )
          }
        >
          Reverse it
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Recording that the money actually left.
 *
 * <p>The bank reference is required, because "refunded" with nothing to trace
 * it by is what a customer disputes three months later.</p>
 */
function PayRefundDialog({ refund, onClose }: { refund: Refund | null; onClose: () => void }) {
  const process = useProcessRefund()
  const [reference, setReference] = useState('')

  return (
    <Dialog open={Boolean(refund)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Pay {refund?.refundNumber}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={process.error} />
        <Alert severity="info" sx={{ mb: 2 }}>
          {formatMoney(refund?.netPayable ?? 0)} to return by {refund?.refundMode}.
        </Alert>
        <TextField
          fullWidth
          required
          size="small"
          label="Bank reference"
          value={reference}
          onChange={(event) => setReference(event.target.value)}
          helperText="The UTR or cheque number the customer can be shown"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!reference.trim() || process.isPending}
          onClick={() =>
            refund &&
            process.mutate(
              { id: refund.id, body: { bankReference: reference.trim() } },
              {
                onSuccess: () => {
                  setReference('')
                  onClose()
                },
              },
            )
          }
        >
          Mark paid
        </Button>
      </DialogActions>
    </Dialog>
  )
}
